// src/ChangelogPage.js
// Admin-only changelog viewer. Fetches CHANGELOG.md from the server and
// renders it with a minimal inline markdown parser.

import { useState, useEffect } from "react";

function renderMarkdown(md) {
  const elements = [];
  const lines = md.split("\n");
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // H1 — title (# ...)
    if (/^# /.test(line)) {
      elements.push(
        <h1 key={key++} style={{ fontSize: 22, fontWeight: 700, color: "#1a3a6e", margin: "0 0 20px" }}>
          {line.slice(2)}
        </h1>
      );
      continue;
    }

    // H2 — date headers (## 2026-06-12)
    if (/^## /.test(line)) {
      elements.push(
        <div key={key++} style={{ fontSize: 17, fontWeight: 700, color: "#1a3a6e", marginTop: 36, marginBottom: 10, paddingBottom: 6, borderBottom: "2px solid #e5e7eb" }}>
          {line.slice(3)}
        </div>
      );
      continue;
    }

    // H3 — Added / Fixed / Improved
    if (/^### /.test(line)) {
      const label = line.slice(4);
      const colors = {
        Added:    { bg: "#E8F5E9", text: "#2e7d32" },
        Fixed:    { bg: "#FFF9C4", text: "#795548" },
        Improved: { bg: "#E3F2FD", text: "#1565C0" },
        Changed:  { bg: "#FFF3E0", text: "#e07b3f" },
      };
      const c = colors[label] || { bg: "#f4f5f7", text: "#555" };
      elements.push(
        <div key={key++} style={{
          display: "inline-block", fontSize: 11, fontWeight: 700,
          background: c.bg, color: c.text,
          padding: "2px 10px", borderRadius: 12,
          marginTop: 14, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em",
        }}>
          {label}
        </div>
      );
      continue;
    }

    // HR (---)
    if (/^---/.test(line)) {
      elements.push(<hr key={key++} style={{ border: "none", borderTop: "1px solid #eee", margin: "28px 0 4px" }} />);
      continue;
    }

    // List item (- ...)
    if (/^- /.test(line)) {
      // Bold prefix: **text** — rest
      const content = line.slice(2);
      const boldMatch = content.match(/^\*\*(.+?)\*\*(.*)$/);
      elements.push(
        <div key={key++} style={{ display: "flex", gap: 8, fontSize: 13, color: "#444", lineHeight: 1.6, marginBottom: 4, paddingLeft: 4 }}>
          <span style={{ color: "#bbb", flexShrink: 0 }}>•</span>
          <span>
            {boldMatch ? (
              <><strong style={{ color: "#1a3a6e" }}>{boldMatch[1]}</strong>{boldMatch[2]}</>
            ) : content}
          </span>
        </div>
      );
      continue;
    }

    // Blank line → small spacer
    if (line.trim() === "") {
      elements.push(<div key={key++} style={{ height: 4 }} />);
      continue;
    }

    // Plain paragraph
    elements.push(
      <p key={key++} style={{ fontSize: 13, color: "#666", lineHeight: 1.7, margin: "4px 0" }}>
        {line}
      </p>
    );
  }

  return elements;
}

export default function ChangelogPage({ onBack }) {
  const [content, setContent] = useState(null);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "auto";
    document.documentElement.style.overflow = "auto";
    return () => {
      document.body.style.overflow = prev;
      document.documentElement.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    fetch("/api/changelog")
      .then(r => r.json())
      .then(d => { if (d.content) setContent(d.content); else setError(d.error || "Failed to load"); })
      .catch(() => setError("Could not connect"));
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#f4f5f7", fontFamily: "Arial, sans-serif", overflowY: "auto" }}>
      {/* Header */}
      <div style={{
        background: "#1a3a6e", padding: "0 28px", height: 56,
        display: "flex", alignItems: "center", gap: 16,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}>
        <button
          onClick={onBack}
          style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", padding: "5px 12px", borderRadius: 6, fontSize: 13, cursor: "pointer" }}
        >
          ← Home
        </button>
        <div>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Changelog</div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>Release history</div>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 740, margin: "0 auto", padding: "36px 20px 60px" }}>
        {error && (
          <div style={{ background: "#fdecea", color: "#c62828", border: "1px solid #f5c6cb", borderRadius: 8, padding: "12px 16px", fontSize: 13 }}>
            {error}
          </div>
        )}
        {!content && !error && (
          <div style={{ textAlign: "center", padding: 60, color: "#aaa" }}>Loading…</div>
        )}
        {content && (
          <div style={{ background: "#fff", borderRadius: 12, padding: "32px 36px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
            {renderMarkdown(content)}
          </div>
        )}
      </div>
    </div>
  );
}
